import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Button, Modal, Typography, CircularProgress } from '@mui/material';

const Camera = ({ isOpen, onClose, onRecordingComplete, recordingTime = 10, exercise }) => {
  const videoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [countdown, setCountdown] = useState(recordingTime);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const recordedChunksRef = useRef([]);
  const [error, setError] = useState(null);

  // Clean up function to stop all media tracks
  const cleanupMediaStream = useCallback(() => {
    console.log('Cleaning up media stream...');
    
    // Stop media recorder if active
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          console.warn('Error stopping media recorder:', e);
        }
      }
      mediaRecorderRef.current = null;
    }
    
    // Stop all media tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => {
        console.log('Stopping track:', track.kind);
        track.stop();
      });
      mediaStreamRef.current = null;
    }
    
    // Clear video source
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Handle recording complete
  const handleRecordingComplete = useCallback((videoBlob) => {
    if (!videoBlob) {
      const err = new Error('No video data received');
      console.error(err);
      setError(err);
      return;
    }

    console.log('=== VIDEO BLOB DETAILS ===');
    console.log('Size:', videoBlob.size, 'bytes');
    console.log('Type:', videoBlob.type);

    try {
      // Create a filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `exercise-${timestamp}.webm`;
      
      const file = new File(
        [videoBlob],
        fileName,
        { 
          type: videoBlob.type || 'video/webm',
          lastModified: Date.now()
        }
      );
      
      console.log('Created file:', file.name, 'size:', file.size, 'type:', file.type);
      
      // Pass the file to the parent component
      onRecordingComplete(file);
      setError(null);
    } catch (err) {
      console.error('Error creating file:', err);
      setError(err);
    }
  }, [onRecordingComplete]);

  // Start recording
  const startRecording = useCallback(async () => {
    if (isRecording) return;
    
    setError(null);
    setRecordedChunks([]);
    recordedChunksRef.current = [];
    
    console.log('=== STARTING RECORDING ===');
    setIsProcessing(true);
    setError(null);
      
    try {
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 30, max: 60 },
          facingMode: 'user',
          aspectRatio: 16/9
        },
        audio: false
      });
      
      // Set up video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        
        // Wait for video to be ready
        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().then(resolve).catch(console.error);
          };
        });
      }
      
      // Store stream for cleanup
      mediaStreamRef.current = stream;
      
      // Set up media recorder options
      const mimeType = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=h264,opus',
        'video/webm',
        'video/mp4',
        '' // Let browser decide
      ].find(type => !type || MediaRecorder.isTypeSupported(type)) || '';
      
      console.log('Using MIME type:', mimeType || 'browser default');
      
      // Create media recorder
      const options = mimeType ? { mimeType } : undefined;
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      
      // Set up event handlers
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          console.log('Received chunk of size:', event.data.size, 'bytes');
          // Update both state and ref
          setRecordedChunks(prev => {
            const newChunks = [...prev, event.data];
            recordedChunksRef.current = newChunks;
            return newChunks;
          });
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        console.log('MediaRecorder stopped');
        setIsRecording(false);
        
        // Process recorded chunks from ref to ensure we have the latest
        const chunks = recordedChunksRef.current;
        if (chunks && chunks.length > 0) {
          console.log(`Creating blob from ${chunks.length} chunks`);
          try {
            const blob = new Blob(chunks, {
              type: chunks[0]?.type || 'video/webm'
            });
            console.log('Blob created successfully, size:', blob.size, 'bytes');
            handleRecordingComplete(blob);
          } catch (error) {
            console.error('Error creating blob:', error);
            setError(error);
          }
        } else {
          console.warn('No recorded chunks available');
          setError(new Error('No video data was recorded'));
        }
        
        // Clean up
        cleanupMediaStream();
        // Clear the chunks
        setRecordedChunks([]);
        recordedChunksRef.current = [];
      };
      
      mediaRecorderRef.current.onerror = (event) => {
        console.error('MediaRecorder error:', event.error || 'Unknown error');
        setError(event.error || new Error('MediaRecorder error'));
        setIsRecording(false);
        cleanupMediaStream();
      };
      
      // Start recording with 1-second timeslice
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setIsProcessing(false);
      
      // Start countdown
      let timeLeft = recordingTime;
      setCountdown(timeLeft);
      
      const countdownInterval = setInterval(() => {
        timeLeft--;
        setCountdown(timeLeft);
        
        if (timeLeft <= 0) {
          clearInterval(countdownInterval);
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
        }
      }, 1000);
      
      // Set a timeout to stop recording after recordingTime seconds
      const recordingTimeout = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
        clearInterval(countdownInterval);
      }, recordingTime * 1000);
      
      // Clean up on unmount
      return () => {
        clearInterval(countdownInterval);
        clearTimeout(recordingTimeout);
      };
      
    } catch (err) {
      console.error('Error starting recording:', err);
      setError(err);
      setIsRecording(false);
      setIsProcessing(false);
      cleanupMediaStream();
    }
  }, [isRecording, recordingTime, recordedChunks, handleRecordingComplete, cleanupMediaStream]);
  
  // Toggle recording
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
    } else {
      startRecording();
      cleanupMediaStream();
    }
  }, [isRecording, cleanupMediaStream]);

  // Clean up on unmount or when isOpen changes
  useEffect(() => {
    if (!isOpen) {
      cleanupMediaStream();
      setRecordedChunks([]);
      recordedChunksRef.current = [];
      setCountdown(recordingTime);
      setError(null);
    }
  }, [isOpen, cleanupMediaStream, recordingTime]);

  // Handle modal close
  const handleClose = useCallback(() => {
    if (isRecording) {
      toggleRecording();
    }
    cleanupMediaStream();
    onClose();
  }, [isRecording, onClose, toggleRecording, cleanupMediaStream]);

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: '800px',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 3,
        outline: 'none',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {isRecording ? `Recording... ${countdown}s` : exercise?.name || 'Camera'}
        </Typography>

        {!isRecording && exercise && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Instructions:
            </Typography>
            <Typography variant="body1">
              {exercise.description}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Click "Start Recording" when you're ready. The recording will automatically stop after {recordingTime} seconds.
            </Typography>
          </Box>
        )}
        
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            Error: {error.message}
          </Typography>
        )}
        
        <Box sx={{
          width: '100%',
          aspectRatio: '16/9',
          bgcolor: 'black',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 1
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scaleX(-1)' // Mirror the video
            }}
          />
          
          {(isProcessing || !isRecording) && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              fontSize: '1.2rem'
            }}>
              {isProcessing ? 'Processing...' : 'Camera Ready'}
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {!isRecording ? (
            <Button
              variant="contained"
              color="primary"
              onClick={startRecording}
              disabled={isProcessing}
              startIcon={
                isProcessing ? <CircularProgress size={20} color="inherit" /> : null
              }
            >
              {isProcessing ? 'Processing...' : 'Start Recording'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              onClick={toggleRecording}
              disabled={!isRecording || isProcessing}
            >
              Stop Recording
            </Button>
          )}
          <Button 
            variant="outlined" 
            onClick={handleClose}
            disabled={isProcessing}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default Camera;
